import {
  PdfGen,
  PdfUnit,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../pdf';
import { GTuple, GLabelType } from '../glabel';
import { GUtil } from '../gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/label';

function fisherYatesShuffle<T>(arr: T[], rnd: { next(a: number, b: number): number }): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = rnd.next(0, i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function drawRope(
  page0: PageGen,
  page1: PageGen,
  rect: PdfRect,
  items: GTuple[],
  map: number[],
  rnd: { next(a: number, b: number): number },
  drawTopNumbers: boolean,
  itemD?: number
): void {
  const count = items.length;
  const f1: PdfFont = { font: PdfFont.FONT_NAME, size: GUtil.baseFont.size / 2 };
  const margin = PdfUnit.FromMillimeters(5);
  const pSize = PdfUnit.FromMillimeters(2);

  const bottomMap = fisherYatesShuffle([...Array(count).keys()], rnd);

  const invBottomMap = new Map<number, number>();
  for (let i = 0; i < bottomMap.length; i++) {
    invBottomMap.set(bottomMap[i], i);
  }
  const invMap = new Map<number, number>();
  for (let i = 0; i < map.length; i++) {
    invMap.set(map[i], i);
  }

  const rectMargin = new PdfRect(
    rect.x + margin,
    rect.y + margin,
    rect.width - 2 * margin,
    rect.height - 2 * margin
  );

  const left1 = new PdfRect(rectMargin.x, rectMargin.y, rectMargin.height, rectMargin.height);
  const right1 = new PdfRect(
    left1.x + left1.width,
    rectMargin.y,
    rectMargin.width - left1.width,
    rectMargin.height
  );

  const right2 = new PdfRect(rectMargin.x, rectMargin.y, right1.width, rectMargin.height);
  const left2 = new PdfRect(right2.x + right2.width, rectMargin.y, left1.width, rectMargin.height);

  const sW = right1.width / count;
  const sH = right1.height;

  const allTexts = items.flatMap((item) => {
    const texts: string[] = [];
    const leftText = item.left.getText();
    const rightText = item.right.getText();
    if (leftText) texts.push(leftText);
    if (rightText) texts.push(rightText);
    return texts;
  });
  const font = page0.adjustFont(sW, allTexts, GUtil.baseFont);

  for (let i = 0; i < count; i++) {
    const isRightText = items[bottomMap[i]].right.type === GLabelType.Text;
    const isLeftNotText = items[map[i]].left.type !== GLabelType.Text;
    const currentItemD = itemD ?? (isRightText && isLeftNotText ? 0.65 : 0.5);

    const r1a = new PdfRect(right1.x + sW * i, right1.y, sW, sH * currentItemD);
    const r1b = new PdfRect(right1.x + sW * i, r1a.y + r1a.height, sW, sH * (1 - currentItemD));

    const p1a = new PdfPoint(r1a.x + sW / 2, r1a.y);
    const p1b = new PdfPoint(r1a.x + sW / 2, r1b.y + r1b.height);
    const rr1a = new PdfRect(p1a.x - pSize / 2, p1a.y - pSize / 2, pSize, pSize);
    const rr1b = new PdfRect(p1b.x - pSize / 2, p1b.y - pSize / 2, pSize, pSize);

    // Page 0: front
    page0.drawLabel(items[map[i]].left, { rect: r1a, font });

    const labelBgBrush = new PdfBrush(PdfColor.fromCmyk(0.4, 0, 0, 0, 0));
    page0.drawRectangle(null, labelBgBrush, r1b);
    page0.drawLabel(items[bottomMap[i]].right, { rect: r1b, font });

    // Page 0: marks
    page0.drawLines(GUtil.cutPen, [
      new PdfPoint(p1a.x - margin, p1a.y - margin),
      p1a,
      new PdfPoint(p1a.x + margin, p1a.y - margin),
    ]);
    page0.drawLines(GUtil.cutPen, [
      new PdfPoint(p1b.x - margin, p1b.y + margin),
      p1b,
      new PdfPoint(p1b.x + margin, p1b.y + margin),
    ]);
    page0.drawEllipse(null, PdfBrush.Black, rr1a);
    page0.drawEllipse(null, PdfBrush.Black, rr1b);

    // Page 1: back
    const p2a = new PdfPoint(right2.x + sW * i + sW / 2, right2.y);
    const p2b = new PdfPoint(right2.x + sW * i + sW / 2, right2.y + right2.height);
    const rr2a = new PdfRect(p2a.x - pSize / 2, p2a.y - pSize / 2, pSize, pSize);
    const rr2b = new PdfRect(p2b.x - pSize / 2, p2b.y - pSize / 2, pSize, pSize);

    page1.drawLines(GUtil.cutPen, [
      new PdfPoint(p2a.x - margin, p2a.y - margin),
      p2a,
      new PdfPoint(p2a.x + margin, p2a.y - margin),
    ]);
    page1.drawLines(GUtil.cutPen, [
      new PdfPoint(p2b.x - margin, p2b.y + margin),
      p2b,
      new PdfPoint(p2b.x + margin, p2b.y + margin),
    ]);
    page1.drawEllipse(null, PdfBrush.Black, rr2a);
    page1.drawEllipse(null, PdfBrush.Black, rr2b);

    // Page 0: top numbers
    if (drawTopNumbers) {
      const numRect = new PdfRect(p1a.x - sW / 2, p1a.y, sW, f1.size);
      page0.drawString(`${i + 1}`, f1, PdfBrush.Black, null, numRect, ETextAlign.TopCenter);
    }

    // Page 1: answer key
    if (i === 0) {
      const p2 = new PdfPoint(right2.x + (count - 1) * sW + sW / 2, right2.y);
      page1.drawLine(GUtil.basePen, new PdfPoint(left2.centerX, left2.centerY), p2);
    } else {
      const i1 = count - (invBottomMap.get(i - 1) ?? 0) - 1;
      const i2 = count - (invMap.get(i) ?? 0) - 1;

      const p1 = new PdfPoint(right2.x + i1 * sW + sW / 2, right2.y + right2.height);
      const p2 = new PdfPoint(right2.x + i2 * sW + sW / 2, right2.y);

      page1.drawLine(GUtil.basePen, p1, p2);
    }
  }

  // Borders
  page0.drawRectangle(GUtil.cutPen, null, rect);
  page1.drawRectangle(GUtil.cutPen, null, rect);

  page0.drawRectangle(GUtil.basePen, null, right1);
  page1.drawRectangle(GUtil.basePen, null, right2);

  // Thread window
  page0.cutCircle(new PdfPoint(left1.centerX, left1.centerY));
  page1.cutCircle(new PdfPoint(left2.centerX, left2.centerY));
}
